import { z } from "zod";
import { GokuOutput } from "./agent-schemas.js";

// z.string().url() accepts javascript:, data:, vbscript: etc. — anything
// `new URL()` parses. jd_url is later passed to window.open() / <a href> in
// the web app, so restrict it to http(s) at the boundary to close that XSS
// vector. Returns a chainable string schema usable with .max(...).
const httpUrl = (maxLen = 2000) =>
  z.string().url().max(maxLen).refine(
    (u) => {
      try {
        const proto = new URL(u).protocol;
        return proto === "http:" || proto === "https:";
      } catch {
        return false;
      }
    },
    { message: "URL must use http(s)" }
  );

// Response from /api/qualify. Discriminated on `kind`:
//   - "existing" → the JD is already in the user's funnel; UI shows status
//                  + link to the existing application, no Goku call ran.
//   - "triage"   → fresh JD; embeds the full GokuOutput shape so callers
//                  treat it identically to the old response.

// ============================================================
// User profile
// ============================================================

export const PatchMeBody = z.object({
  display_name: z.string().min(1).max(100).optional(),
  short_description: z.string().max(2000).optional(),
  primary_profile_id: z.string().max(40).optional(),
  // Days-in-stage aging: the single threshold N (days) that drives the kanban
  // colors and the opt-in auto-archive sweep.
  stale_days: z.number().int().min(1).max(365).optional(),
  auto_archive_enabled: z.boolean().optional(),
});

export type PatchMeBody = z.infer<typeof PatchMeBody>;

// ============================================================
// Onboarding
// ============================================================

export const OnboardConfirmBody = z.object({
  pending_cv_id: z.string().min(1),
  display_name: z.string().min(1).max(100),
  short_description: z.string().min(1).max(2000),
  profile_name: z.string().min(1).max(100),
  profile_description: z.string().min(1).max(2000),
});

export type OnboardConfirmBody = z.infer<typeof OnboardConfirmBody>;

// ============================================================
// Profiles
// ============================================================

export const PatchProfileBody = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().min(1).max(2000).optional(),
  is_primary: z.boolean().optional(),
});

export type PatchProfileBody = z.infer<typeof PatchProfileBody>;

// ============================================================
// Applications · core
// ============================================================

export const QualifyBody = z.object({
  jd_url: httpUrl(2000),
  page_html: z.string().max(500_000),
  page_text: z.string().max(200_000).optional(),
});

export type QualifyBody = z.infer<typeof QualifyBody>;

// Returned by /api/qualify when the JD is already in the user's funnel
// (same canonical URL as an existing application). Popup shows the
// "already in your funnel" panel instead of spending a Goku call.
export const ExistingApplicationRef = z.object({
  id: z.string(),
  job_title: z.string(),
  company: z.string(),
  status: z.string(),
  fit_score: z.number().int().nullable(),
  created_at: z.number().int(),
});

export type ExistingApplicationRef = z.infer<typeof ExistingApplicationRef>;

export const QualifyResponse = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("existing"),
    existing_application: ExistingApplicationRef,
  }),
  GokuOutput.extend({ kind: z.literal("triage") }),
]);

export type QualifyResponse = z.infer<typeof QualifyResponse>;

export const CreateApplicationBody = z.object({
  qualify_result: GokuOutput,
  profile_id: z.string().min(1),
  user_warned_low_fit: z.boolean().default(false),
  jd_url: httpUrl(2000),
  jd_text: z.string().max(200_000).optional(),
  // When true and the URL matches an application the user already has, refill
  // the just-extracted jd_text and re-enqueue the pipeline (Bulma → Vegeta →
  // Cor-Petit). Without this, hitting the same JD again returns "existing"
  // with no side effects. Set by the extension when the user explicitly opts
  // into regenerating.
  regenerate: z.boolean().optional().default(false),
});

export type CreateApplicationBody = z.infer<typeof CreateApplicationBody>;

export const ApplicationStatusEnum = z.enum([
  "saved",
  "applying_in_progress",
  "applied",
  "interviewing",
  "offer",
  "closed_won",
  "closed_rejected",
  "closed_discarded",
]);

export type ApplicationStatus = z.infer<typeof ApplicationStatusEnum>;

export const PatchApplicationBody = z.object({
  status: ApplicationStatusEnum.optional(),
  notes: z.string().max(10_000).optional(),
  profile_id: z.string().optional(),
  job_title: z.string().min(1).max(300).optional(),
});

export type PatchApplicationBody = z.infer<typeof PatchApplicationBody>;

// ============================================================
// Applications · apply tracking
// ============================================================

export const DismissApplyBody = z.object({
  action: z.enum(["still_working", "decided_not"]),
});

export type DismissApplyBody = z.infer<typeof DismissApplyBody>;

// ============================================================
// Applications · materials
// ============================================================

export const PatchMaterialsBody = z.object({
  cover_letter: z.string().max(20_000).optional(),
  q_why_role: z.string().max(5000).optional(),
  q_salary: z.string().max(2000).optional(),
  q_notice: z.string().max(2000).optional(),
  q_why_now: z.string().max(5000).optional(),
  // The salary figure the user actually put in the application form, kept
  // as a record (and plotted against the market range on JobDetail).
  // null clears it. Capped at a sane upper bound to reject fat-finger input.
  user_target_salary: z.number().int().min(0).max(100_000_000).nullable().optional(),
});

export type PatchMaterialsBody = z.infer<typeof PatchMaterialsBody>;

// ============================================================
// Extension
// ============================================================

export const ExtensionPingBody = z.object({
  version: z.string().max(20),
});

export type ExtensionPingBody = z.infer<typeof ExtensionPingBody>;

// One row per application returned by GET /api/extension/markers. The
// extension uses these to highlight job listings on any page where the
// user has already engaged with the role.
//
// Both `company_norm` and `title_norm` are computed server-side via
// `@jobwarp/shared` normalize helpers so the extension can match
// case-insensitively without reimplementing the rules.
export const AppliedJobMarker = z.object({
  application_id: z.string(),
  jd_url_canonical: z.string().nullable(),
  company_norm: z.string(),
  title_norm: z.string(),
  status: ApplicationStatusEnum,
  fit_score: z.number().int().nullable(),
});

export type AppliedJobMarker = z.infer<typeof AppliedJobMarker>;

export const MarkersResponse = z.object({
  markers: z.array(AppliedJobMarker),
  // Server-controlled kill switch: extension skips these hostnames entirely.
  // Substring match (so "linkedin.com" disables www.linkedin.com too).
  disabled_hosts: z.array(z.string()),
  // Max textContent length of a matched ancestor — prevents badging the
  // whole <main> on JD detail pages or unrelated articles.
  max_match_ancestor_chars: z.number().int().positive(),
  // Token-set ratio threshold for fuzzy title match (0..1).
  fuzzy_title_threshold: z.number().min(0).max(1),
});

export type MarkersResponse = z.infer<typeof MarkersResponse>;

// ============================================================
// Error code shapes (kept consistent across worker + clients)
// ============================================================

export type ApiError = {
  error: string;
  code:
    | "unauthenticated"
    | "forbidden"
    | "not_found"
    | "validation_error"
    | "rate_limit_exceeded"
    | "llm_error"
    | "internal_error";
};
